import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { PluckCard } from "@/components/pluck-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { track } from "@/lib/analytics";
import { trpc } from "@/lib/trpc";

const PAGE_SIZE = 40;
const SKELETON_COUNT = 8;
const skeletonKeys = Array.from({ length: SKELETON_COUNT }, (_, i) => `s${i}`);

export function LibraryPage() {
	const [domain, setDomain] = useState<string | null>(null);
	const query = useInfiniteQuery(
		trpc.plucks.list.infiniteQueryOptions(
			{ limit: PAGE_SIZE, domain: domain ?? undefined },
			{
				getNextPageParam: (last) => last.nextCursor ?? undefined,
			}
		)
	);

	const items = useMemo(
		() => query.data?.pages.flatMap((p) => p.items) ?? [],
		[query.data]
	);

	const domains = useMemo(() => {
		const counts = new Map<string, number>();
		for (const item of items) {
			counts.set(item.sourceDomain, (counts.get(item.sourceDomain) ?? 0) + 1);
		}
		return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
	}, [items]);

	// Once per visit: the first page's size (not the domains: those are the
	// user's browsing).
	const viewed = useRef(false);
	const firstPage = query.data?.pages[0]?.items.length;
	const hasMore = query.hasNextPage;
	useEffect(() => {
		if (firstPage === undefined || viewed.current) {
			return;
		}
		viewed.current = true;
		track("library.viewed", { first_page: firstPage, has_more: hasMore });
		if (firstPage === 0) {
			track("onboarding.empty_state_viewed", { surface: "library" });
		}
	}, [firstPage, hasMore]);

	const filterBy = (next: string | null) => {
		setDomain(next);
		track("library.filtered", {
			filter: next === null ? "all" : "domain",
			domain_rank:
				next === null ? undefined : domains.findIndex(([d]) => d === next),
		});
	};

	return (
		<div className="mx-auto flex max-w-6xl flex-col gap-6 p-8">
			<header className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="font-display font-extrabold text-2xl tracking-[-0.02em]">
						Library
					</h1>
					<p className="text-muted-foreground text-sm">
						Everything you pluck from the web lands here.
					</p>
				</div>
			</header>

			{(domains.length > 1 || domain) && (
				<div className="flex flex-wrap gap-1.5">
					<Button
						onClick={() => filterBy(null)}
						size="sm"
						variant={domain === null ? "default" : "outline"}
					>
						All
					</Button>
					{domains.map(([d, count]) => (
						<Button
							key={d}
							onClick={() => filterBy(d)}
							size="sm"
							variant={domain === d ? "default" : "outline"}
						>
							{d}
							<span className="text-muted-foreground tabular-nums">
								{count}
							</span>
						</Button>
					))}
				</div>
			)}

			{query.isPending && (
				<div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
					{skeletonKeys.map((k) => (
						<Skeleton className="aspect-[4/3] rounded-xl" key={k} />
					))}
				</div>
			)}
			{!query.isPending && items.length === 0 && (
				<EmptyLibrary filtered={domain !== null} />
			)}
			{items.length > 0 && (
				<>
					<div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
						{items.map((pluck) => (
							<PluckCard key={pluck.id} pluck={pluck} />
						))}
					</div>
					{query.hasNextPage && (
						<div className="flex justify-center">
							<Button
								disabled={query.isFetchingNextPage}
								onClick={() => {
									track("library.load_more", {
										surface: "library",
										loaded: items.length,
									});
									query.fetchNextPage();
								}}
								variant="outline"
							>
								{query.isFetchingNextPage ? "Loading…" : "Load more"}
							</Button>
						</div>
					)}
				</>
			)}
		</div>
	);
}

function EmptyLibrary({ filtered }: { filtered: boolean }) {
	return (
		<div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-12 text-center">
			<p className="font-medium">
				{filtered ? "No plucks from this site yet." : "Your library is empty."}
			</p>
			<p className="max-w-md text-muted-foreground text-sm">
				Install the Pluck extension, sign in with this account, and pluck any
				component on any website. Each pluck is saved here with its screenshot
				and metadata, ready to drop onto a board.
			</p>
		</div>
	);
}
